import type { TFunction } from 'src/hooks/use-locales/types';

import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import isUndefinedOrNull from '@ahd/shared/tools/is-undefined-or-null';
import { type ReactNode, useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, createTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import Iconify from 'src/components/iconify';

import { type ConfigObject } from '../../_configs/types';
import { ErrorAction } from '../../_tools/types/function-output-type';
import { type Modules } from '../../modules/_definition/types';
import generateLink from '../../utils/generateLink';
import enhancedNavigate from '../../utils/tools/enhanced-navigate';

interface ErrorCardProps {
    action?: ErrorAction;
    collapse?: {
        code: string;
        message: string;
        stack?: string;
    };
    description?: ReactNode;
    heading: ReactNode;
    level: 'error' | 'grey' | 'info' | 'primary';
    module: Modules;
    resetErrorState?: () => void;
    settings: ConfigObject;
    translate: TFunction;
}

function ErrorCard(props: ErrorCardProps) {
    const { action, collapse, description, heading, level, module, resetErrorState, settings, translate } = props;
    const [collapseError, setCollapseError] = useState<boolean>(false);

    const theme = createTheme();
    const colors = {
        light: level === 'grey' ? theme?.palette?.[level]?.[300] : theme?.palette?.[level]?.light,
        main: level === 'grey' ? theme?.palette?.[level]?.[500] : theme?.palette?.[level]?.main,
        dark: level === 'grey' ? theme?.palette?.[level]?.[700] : theme?.palette?.[level]?.dark,
    };

    return (
        <Box data-testid="error-card" sx={{ backgroundColor: alpha(colors.main, 0.05), borderRadius: 2, borderColor: colors.light, borderStyle: 'solid', borderWidth: 2, p: 2 }}>
            <Stack alignItems="center" direction="row" gap={2}>
                {level === 'error' ? (
                    <Iconify icon="mdi:alert-circle-outline" fontSize="large" sx={{ color: colors.main }} />
                ) : (
                    <Iconify icon="mdi:information-outline" fontSize="large" sx={{ color: colors.main }} />
                )}
                <Stack gap={1} sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: colors.dark + '!important' }}>
                        {heading}
                    </Typography>
                    {!isUndefinedNullOrEmpty(description) && (
                        <Typography variant="body2" sx={{ color: colors.dark + '!important' }}>
                            {description}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" flexWrap="nowrap">
                    {!isUndefinedNullOrEmpty(action) && action !== ErrorAction.NONE && !isUndefinedOrNull(resetErrorState) && (
                        <Tooltip title={translate?.(`errors:actions.${action}`)}>
                            <IconButton
                                onClick={function () {
                                    if (action === ErrorAction.RELOAD) location.reload();
                                    // else if (action === 'exitError') this.setState({ error: null });
                                    else if (action === ErrorAction.HOME) {
                                        resetErrorState();
                                        enhancedNavigate(generateLink({ allFacets: undefined, filter: undefined, module, pathOnly: false, settings, type: 'filter' }));
                                    }
                                }}
                            >
                                {action === ErrorAction.RELOAD ? (
                                    <Iconify icon="mdi:refresh" color={level === 'grey' ? 'primary' : level} />
                                ) : /* action === 'exitError' ? <Iconify icon="mdi:close" /> : */ action === ErrorAction.HOME ? (
                                    <Iconify icon="mdi:home" color={level === 'grey' ? 'primary' : level} />
                                ) : null}
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isUndefinedNullOrEmpty(collapse) && (
                        <IconButton color={level === 'grey' ? 'default' : level} onClick={() => setCollapseError(!collapseError)}>
                            <Iconify
                                icon="solar:alt-arrow-down-line-duotone"
                                color={level === 'grey' ? 'primary' : level}
                                sx={{ transform: collapseError ? 'rotate(180deg)' : undefined }}
                            />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
            {!isUndefinedNullOrEmpty(collapse) && (
                <Collapse in={collapseError}>
                    <Divider sx={{ backgroundColor: colors.light + '!important', mt: '16px!important', mb: '8px!important' }} />
                    <Stack gap={1}>
                        <Typography variant="caption" sx={{ color: colors.dark + '!important' }}>
                            {collapse.code}
                        </Typography>
                        <Typography variant="h6" sx={{ color: colors.dark + '!important' }}>
                            {collapse.message}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.dark + '!important' }}>
                            {collapse.stack}
                        </Typography>
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
}

export default ErrorCard;
